import { action, computed, observable } from 'mobx'
import Fuse, { FuseResult } from 'fuse.js'
import { $RootStore } from '~/stores/RootStore'
import { debounce } from '../../../shared-lib/utils'
import { SearchExternalClientsQuery } from '../../../graphQL/generated/client'

export class BaseSearchStore {
  rootStore: $RootStore
  sections: string[] = ['SCREENS', 'CLIENTS'] // overwrite in extending class if adding a new section
  @observable accessor _searchVisible = false
  @observable accessor _searchTerm = ''
  @observable accessor searchFocusable = true
  @observable accessor searchControllers: SectionSearchController<FuseResult<screenSearchResult> | { item: screenSearchResult }>[] = []
  debounce_track_search: () => void

  constructor(rootStore: $RootStore) {
    this.rootStore = rootStore
    this.debounce_track_search = debounce(() => {
      this.trackSearch()
    }, 400)
  }

  @action setSearchTerm = (searchTerm: string) => {
    this._searchTerm = searchTerm
    if (this.searchTerm.length > 0) {
      this.showSearch()
    }
  }

  triggerSearches = () => {
    this.searchControllers.forEach(controller => controller.search())
    this.debounce_track_search()
  }

  showSearch = () => {
    //implement in extending class
  }

  dismissSearch = () => {
    //implement in extending class
  }

  @action hideSearch = () => {
    this._searchTerm = ''
    this._searchVisible = false
  }

  trackSearch = () => {
    //implement in extending class
  }

  @computed get searchTerm() {
    return this._searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  }

  @computed get searchVisible() {
    const hasResults = Object.values(this.resultCounts).reduce((a, b) => a + b, 0) !== 0
    const hasUiToShow = this.showNoResults || this.searchLoading || this.searchTerm.length === 0 || hasResults
    return this._searchVisible && hasUiToShow
  }

  @computed get showNoResults() {
    return this._searchVisible && this.noSearchResults
  }

  @computed get searchLoading() {
    return this.searchControllers.some(controller => controller.loading)
  }

  @computed get resultCounts() {
    const resultCounts: Record<string, number> = {}
    this.searchResults.forEach(section => resultCounts[section.title] = section.data.length)
    return resultCounts
  }

  @computed get noSearchResults() {
    const searchLoaded = this.searchControllers.every(controller => !controller.loading)
    const hasNoResults = Object.values(this.resultCounts).reduce((a, b) => a + b, 0) === 0
    return this.searchTerm.length > 0 && hasNoResults && searchLoaded
  }

  @computed get searchResults() {
    if (this.searchTerm === '') return []

    return this.searchControllers
      .map(controller => ({
        title: controller.section,
        data: controller.results,
        loading: controller.loading,
      }))
  }
}

export class SectionSearchController<T> {
  @observable accessor loading = false
  @observable accessor results: T[] = []
  @observable accessor error: Record<string, any> = {}
  debounced_fetch: () => void
  store: $RootStore
  section = ''

  constructor(section: string, store: $RootStore) {
    this.section = section
    this.store = store
    this.debounced_fetch = () => false
  }

  @action setLoading = (loading: boolean) => {
    this.loading = loading
  }

  @action search = async (): Promise<T[]> => {
    this.setLoading(true)

    this.setLoading(false)
    return this.results
  }

  @computed get searchTerm() {
    return this.store.searchStore.searchTerm
  }

  showDropDownError = () => {
    const {
      dropdownMessageHandler: { showDropdownMessage },
      i18n: { s },
      searchStore: { searchVisible },
    } = this.store
    if (!searchVisible) return
    showDropdownMessage({
      title: s(`There was an error searching ${this.section}`),
      message: s(`something went wrong: ${this.error.message}`),
      type: 'error',
      closeInterval: 1500,
    })
  }
}

export class BaseClientSectionSearchController extends SectionSearchController<{ item: screenSearchResult }> {
  constructor(section: string, store: $RootStore) {
    super(section, store)
    this.debounced_fetch = debounce(() => {
      this.fetchClientSearchResults()
    }, 500)
  }

  @action search = async () => {
    this.loading = true
    this.results = []
    this.error = {}
    this.debounced_fetch()
    return this.results
  }

  @action fetchClientSearchResults = async () => {
    const { communication } = this.store
    try {
      const searchResults = await communication.requester.searchExternalClients({
        search: this.searchTerm,
      })
      this.processClientSearchResults(searchResults)

    } catch (error: any) {
      this.error = error
      this.results = []
      this.showDropDownError()
    } finally {
      this.setLoading(false)
    }
  }

  @action processClientSearchResults = (searchResults: SearchExternalClientsQuery) => {
    const { searchExternalClients } = searchResults
    if (!searchExternalClients || searchExternalClients.length === 0) {
      this.results = []
      return
    }
    this.store.externalClientStore.mergeData(searchExternalClients)
    if (!searchExternalClients) return

    const results = this.formatExternalClientSearchResult(searchExternalClients)
    this.results = results
  }

  formatExternalClientSearchResult = (_clients: SearchExternalClientsQuery['searchExternalClients']): { item: screenSearchResult; }[] => {
    //implement in extending class
    return []
  }
}

export class BaseScreenSectionSearchController extends SectionSearchController<FuseResult<screenSearchResult>> {

  constructor(
    section: string,
    store: $RootStore,
  ) {
    super(section, store)
  }

  @computed get fuse() {
    return new Fuse(this.screenData, fuseOptions)
  }

  @computed get screenData() {
    return [...this.generalSearchData, ...this.tapToPayScreenData].filter(item => this.canReach(item))
  }

  protected canReach = (_item: any): boolean => true

  @computed get generalSearchData() {
    const { general, soloOnly, secretSpaOnly } = this.groupedSearchData
    const results: screenSearchResult[] = [...(general ?? [])]
    if (this.store.authStore.isSaasUser) {
      results.push(...(soloOnly ?? []))
    } else {
      results.push(...(secretSpaOnly ?? []))
    }
    return results
  }

  @computed get tapToPayScreenData(): Array<any> {
    //implement in extending class
    return []
  }

  @computed get groupedSearchData() {
    const groupedSearchData: Partial<Record<ScreenDataConditions, any>> = {}
    const searchTerms = this.store.searchManifestStore.activeTerms
    searchTerms.forEach(searchItem => {
      const grouping = (searchItem.conditions as ScreenDataConditions | undefined) ?? ScreenDataConditions.General
      groupedSearchData[grouping] ?
        groupedSearchData[grouping].push(searchItem)
        : groupedSearchData[grouping] = [searchItem]
    })
    return groupedSearchData
  }

  @action search = async () => {
    this.loading = true
    if (this.searchTerm) {
      this.results = this.fuse.search(this.searchTerm).map(result => this.formatScreenSearchResult(result))
    } else {
      this.results = []
    }
    this.loading = false
    return this.results
  }

  // overwrite in extending class to set correct icon and path
  formatScreenSearchResult = (result: FuseResult<screenSearchData>): FuseResult<screenSearchResult> => {
    const { item, ...fuseResultRest } = result
    const { address: { appPath, desktopPath }, appIcon, desktopIcon, ...rest } = item
    return {
      ...fuseResultRest,
      item: {
        ...rest,
        address: {
          path: appPath ?? desktopPath,
          params: {},
        },
        icon: appIcon ?? desktopIcon,
        type: SearchResultTypes.Screen,
      },
    }
  }
}

export const fuseOptions = {
  keys: ['primaryTerm', 'synonyms'],
  threshold: 0.3,
  minMatchCharLength: 2,
}

export interface screenSearchData {
  primaryTerm: string
  synonyms?: string[]
  address: {
    appPath: string,
    desktopPath: string,
    params?: Record<string, any>,
  }
  appIcon: string
  desktopIcon: string
  conditions?: ScreenDataConditions | string
  type?: SearchResultTypes
  color?: string
}

export interface screenSearchResult {
  primaryTerm: string
  synonyms?: string[]
  address: {
    path: string,
    params?: Record<string, any>,
  }
  icon: string
  type?: SearchResultTypes
  color?: string
}

export enum SearchResultTypes {
  Client = 'CLIENTS',
  Screen = 'SCREENS',
  Guide = 'GUIDES',
}

enum ScreenDataConditions {
  General = 'general',
  SecretSpaOnly = 'secretSpaOnly',
  SoloOnly = 'soloOnly',
  TapToPay = 'tapToPay',
}